import { Section } from '../components/Section.js';
import { formatDay, formatTimeRange, timezoneLabel } from '../lib/dates.js';
import {
  type SiteSession,
  type SiteTrack,
  comingSoonCopy,
  groupSessionsByDay,
  sectionState,
} from '../lib/site.js';
import { titleFor, useDocumentTitle } from '../lib/useDocumentTitle.js';
import { useSite } from './EventLayout.js';

export function SessionsPage() {
  const payload = useSite();
  // 'Schedule' is what the nav tab and the heading call it — the tab title
  // should not be the one place inventing a different word for the same page.
  useDocumentTitle(titleFor('Schedule', payload.event.name));
  const state = sectionState(payload.sessions);
  const tracks = new Map((payload.tracks ?? []).map((t) => [t.id, t]));
  const timezone = payload.event.timezone;

  return (
    <div className="animate-fade-up">
      <h2 className="sr-only">Schedule</h2>
      <Section
        state={state}
        comingSoon={comingSoonCopy(payload)}
        emptyTitle="Nothing scheduled yet"
        emptyBody="The programme is still being put together. It will appear here as soon as sessions are confirmed."
      >
        {(sessions) => (
          <>
            <p className="e67-eyebrow">{timezoneLabel(timezone)}</p>
            <div className="mt-5 space-y-10">
              {groupSessionsByDay(sessions, timezone).map((day) => (
                <section key={day.key ?? 'undated'} aria-labelledby={`day-${day.key ?? 'tbc'}`}>
                  <h3
                    id={`day-${day.key ?? 'tbc'}`}
                    className="e67-title sticky top-0 z-10 -mx-4 bg-brand-bg px-4 py-2 text-lg sm:mx-0 sm:px-0"
                  >
                    {day.key
                      ? formatDay(day.sessions[0]?.startAt, timezone)
                      : 'Time to be confirmed'}
                  </h3>
                  <ul className="mt-3 space-y-3">
                    {day.sessions.map((session) => (
                      <li key={session.id}>
                        <SessionCard
                          session={session}
                          track={session.trackId ? tracks.get(session.trackId) : undefined}
                          timezone={timezone}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

function SessionCard({
  session,
  track,
  timezone,
}: {
  session: SiteSession;
  track: SiteTrack | undefined;
  timezone: string;
}) {
  const time = formatTimeRange(session.startAt, session.endAt, timezone);
  return (
    <article className="e67-card px-5 py-5 sm:px-6">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {time ? (
          <time
            className="font-semibold tabular-nums text-brand-on-surface"
            dateTime={session.startAt ?? undefined}
          >
            {time}
          </time>
        ) : (
          <span className="text-brand-muted">Time to be confirmed</span>
        )}
        {track && (
          <span
            className="inline-flex items-center gap-1.5 rounded-brand-sm bg-brand-soft px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-on-surface"
            // A track colour is an organiser-chosen hex on a Kysely text
            // column; if it is anything else the dot simply does not render.
            style={
              track.color && /^#[0-9a-fA-F]{6}$/.test(track.color)
                ? { boxShadow: `inset 3px 0 0 0 ${track.color}` }
                : undefined
            }
          >
            {track.name}
          </span>
        )}
        {session.room && <span className="text-[13px] text-brand-muted">{session.room}</span>}
      </div>
      <h4 className="e67-title mt-2 text-[17px] text-pretty">{session.title}</h4>
      {session.description && (
        <p className="e67-prose mt-1.5 text-[14px] text-pretty">{session.description}</p>
      )}
    </article>
  );
}
